import { Component } from '@theme/component';
import { FilterUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * DROOL collection filters (snippets/wd-collection-filters.liquid).
 *
 * The server renders every filter as a plain link, so everything works without the script. With it,
 * links that stay on this collection update the page in place: the URL changes (pushState, so
 * back/forward and sharing work) and the theme's section renderer morphs the collection section,
 * the same way the stock filters do. Below 990px the panel is a drawer: tapping values and a sort collects them in a draft, the primary button
 * shows how many prints the draft matches, and one tap applies it. The count comes from
 * templates/collection.wd-filter-count.liquid (`?view=wd-filter-count`), a few bytes of JSON.
 *
 * URL model (same as the snippet): one tag per group, all in the collection path, sorted
 * alphabetically because Shopify 301-redirects any other order. Tags that belong to no group
 * (`extras`) and loose `?filter.p.tag=` values (`tags`) are kept as they are.
 *
 * Search & Discovery source (snippets/wd-collection-filters-app.liquid): the groups are Shopify's
 * own filters (`?filter.p.m.custom.filter_medium=…`, price, availability). `app` holds the page's
 * pairs; the drawer's draft keeps its own list, several values per filter.
 *
 * @typedef {{ base: string, extras: string[] | null, tags: string[] | null, vendors: string[] | null,
 *   sort: string | null, app?: string, seeOne: string, seeOther: string }} State
 * @typedef {{ groups: Map<string, string>, vendor: string | null, sort: string,
 *   app?: [string, string][] }} Draft
 *
 * @extends {Component}
 */
const DESKTOP = window.matchMedia('(min-width: 990px)');
/** On <html>, outside the section, so an in-place update (morph) keeps them. */
const SIDEBAR_HIDDEN = 'wd-cf-sidebar-hidden';
const BAR_STUCK = 'wd-cf-bar-stuck';

/**
 * The theme keeps the sticky header's height in --header-height on <body>; a sticky daily offer bar
 * under it (sections/wd-daily-offer.liquid) adds --wd-offer-bar-height. Both cover the page top.
 */
const headerHeight = () => {
  const style = getComputedStyle(document.body);
  return (
    (parseFloat(style.getPropertyValue('--header-height')) || 0) +
    (parseFloat(style.getPropertyValue('--wd-offer-bar-height')) || 0)
  );
};
const COUNT_DELAY = 250;
const COUNT_PLACEHOLDER = '999999';

/**
 * Shopify's `handleize` for tag values: "Grey & Silver" -> "grey-silver".
 * @param {string} value
 */
const handleize = (value) =>
  value
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Lower case without accents: "Célestin" -> "celestin".
 * @param {string} value
 */
const fold = (value) => value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/**
 * A Search & Discovery parameter: any `filter.` one except Tags and Vendor, which keep their own handling.
 * @param {string} key
 */
const isAppParam = (key) => key.startsWith('filter.') && key !== 'filter.p.tag' && key !== 'filter.p.vendor';

/**
 * "&filter.p.m.custom.filter_medium=Photography&…" -> [[name, value], …], decoded.
 * @param {string} [query]
 * @returns {[string, string][]}
 */
const appPairs = (query = '') => [...new URLSearchParams(query.replace(/^&/, ''))].filter(([key]) => isAppParam(key));

/**
 * Same pairs, in any order.
 * @param {[string, string][]} [a]
 * @param {[string, string][]} [b]
 */
const samePairs = (a = [], b = []) =>
  a.length === b.length && a.every(([key, value]) => b.some(([other, otherValue]) => key === other && value === otherValue));

/**
 * A typed amount as the price filter wants it: "1,234.50" or "1.234,50" -> "1234.50"; '' when empty.
 * @param {string} text
 */
const toAmount = (text) => {
  const digits = text.replace(/[^\d.,]/g, '');
  const decimals = digits.match(/[.,](\d{1,2})$/);
  const amount = decimals
    ? `${digits.slice(0, -decimals[0].length).replace(/[.,]/g, '')}.${decimals[1]}`
    : digits.replace(/[.,]/g, '');
  return amount !== '' && Number.isFinite(Number(amount)) ? amount : '';
};

class WdCollectionFilters extends Component {
  requiredRefs = ['state', 'panel', 'openButton', 'closeButton', 'applyButton', 'scrim'];

  /** @type {State | null} */
  #state = null;

  /** @type {Draft} What this page shows, read once from the server-rendered chips. */
  #current = { groups: new Map(), vendor: null, sort: '' };

  /** @type {Draft} */
  #draft = { groups: new Map(), vendor: null, sort: '' };

  /** @type {AbortController | null} */
  #countRequest = null;

  /** @type {IntersectionObserver | null} */
  #stuckObserver = null;

  /** @type {number | undefined} */
  #countTimer;

  /** Bumped per in-place navigation, so a slow response never overwrites a newer one. */
  #navigation = 0;

  /** requestAnimationFrame id of a pending #fitSidebar run (0 = none). */
  #fitFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#readState();
    if (this.#moveTagParamsToPath()) return;
    this.#revealActiveQuickLink();

    DESKTOP.addEventListener('change', this.#onBreakpoint);
    window.addEventListener('popstate', this.#onPopState);
    window.addEventListener('scroll', this.#fitSidebar, { passive: true });
    window.addEventListener('resize', this.#fitSidebar);
    // The bundle bar appears or changes with the cart.
    document.addEventListener('cart:update', this.#fitSidebar);
    document.addEventListener('cart:updated', this.#fitSidebar);
    this.#fitSidebar();
    this.#watchStuck();
    document.addEventListener('wd:offer-bar-height', this.#onBarHeight);

    // Without the script the Filter button opens the panel through :target. If the page was
    // loaded that way, hand over to the scripted drawer.
    if (window.location.hash === `#${this.refs.panel.id}` && !DESKTOP.matches) {
      this.#clearHash();
      this.open();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    DESKTOP.removeEventListener('change', this.#onBreakpoint);
    window.removeEventListener('popstate', this.#onPopState);
    window.removeEventListener('scroll', this.#fitSidebar);
    window.removeEventListener('resize', this.#fitSidebar);
    document.removeEventListener('cart:update', this.#fitSidebar);
    document.removeEventListener('cart:updated', this.#fitSidebar);
    cancelAnimationFrame(this.#fitFrame);
    this.#stuckObserver?.disconnect();
    document.removeEventListener('wd:offer-bar-height', this.#onBarHeight);
    document.documentElement.classList.remove(BAR_STUCK);
    this.#unlock();
  }

  /** After an in-place update the section morph rewrote the markup: read the new state. */
  updatedCallback() {
    super.updatedCallback();

    this.#readState();
    this.#draft = { ...this.#current, groups: new Map(this.#current.groups) };
    this.#revealActiveQuickLink();
    this.#fitSidebar(); // the morph drops the panel's inline height
  }

  /**
   * Desktop sidebar: keeps the panel no taller than the room between its top and the bottom of the
   * window, or the top of the fixed bundle bar (bundle-widget) when that shows. Its group list then
   * scrolls top to bottom inside what is visible. The CSS cap alone assumed the stuck position, so
   * before the sidebar stuck (top of the page) and under the bundle bar the last groups were out of
   * view and could not be reached. One measurement per frame at most.
   */
  #fitSidebar = () => {
    if (this.#fitFrame) return;
    this.#fitFrame = requestAnimationFrame(() => {
      this.#fitFrame = 0;
      const panel = this.refs.panel;
      if (!panel) return;
      if (!DESKTOP.matches) {
        panel.style.removeProperty('--wd-cf-panel-max');
        return;
      }
      const top = Math.max(panel.getBoundingClientRect().top, headerHeight() + 16);
      const bar = document.querySelector('bundle-widget:not([hidden])');
      const barTop = bar instanceof HTMLElement && bar.offsetHeight ? bar.getBoundingClientRect().top : window.innerHeight;
      const bottom = Math.min(window.innerHeight, barTop) - 16;
      panel.style.setProperty('--wd-cf-panel-max', `${Math.max(240, Math.round(bottom - top))}px`);
    });
  };

  /**
   * Reads the state and the selection the server rendered. The morph calls updatedCallback() on
   * the parsed copy of this element, not on this one, so open() reads them again as well.
   */
  #readState() {
    try {
      this.#state = JSON.parse(this.refs.state.textContent ?? '');
    } catch (error) {
      window.droolReportError?.('wd-collection-filters', String(error));
    }
    this.#current = this.#readSelection();
  }

  /**
   * Quick links, chips, Clear all: update in place instead of loading a page.
   * @param {MouseEvent} event
   */
  go(event) {
    const link = /** @type {HTMLAnchorElement} */ (event.target);
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    if (!this.#isInPlace(link.href)) return;

    event.preventDefault();
    this.#navigate(link.href);
  }

  /** @param {string} href */
  #isInPlace(href) {
    if (!this.#state || window.Shopify?.designMode) return false;
    const url = new URL(href, window.location.href);
    return url.origin === window.location.origin && url.pathname.startsWith(this.#state.base);
  }

  /**
   * Shows `href` without a page load. Falls back to a normal page load if anything fails.
   * @param {string} href
   * @param {{ push?: boolean }} [options] - push: false when the URL is already current (back/forward).
   */
  async #navigate(href, { push = true } = {}) {
    const url = new URL(href, window.location.href);
    url.searchParams.delete('page');
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      window.location.assign(url.toString());
      return;
    }

    const navigation = ++this.#navigation;
    if (this.#isOpen) this.close();
    if (push) history.pushState({ wdCollectionFilters: true }, '', url.toString());

    const wrapper = this.closest('.collection-wrapper');
    wrapper?.classList.add('wd-cf-loading');
    this.#scrollToTop();

    try {
      this.dispatchEvent(new FilterUpdateEvent(url.searchParams));
      await sectionRenderer.renderSection(sectionId);
      this.#fitSidebar(); // the morph dropped the panel's inline height
      // The grid was re-rendered (wd-plp-pin.js listens; paginated-list.js fires the same event).
      if (navigation === this.#navigation) {
        document.dispatchEvent(new CustomEvent('wd:grid-update', { detail: { reason: 'render' } }));
      }
    } catch (error) {
      if (navigation !== this.#navigation) return;
      window.droolReportError?.('wd-collection-filters', String(error));
      window.location.assign(url.toString());
    } finally {
      if (navigation === this.#navigation) this.closest('.collection-wrapper')?.classList.remove('wd-cf-loading');
    }
  }

  /** Brings the top of the filter bar back into view when the shopper had scrolled past it. */
  #scrollToTop() {
    const anchor = this.querySelector('.wd-cf__quick') ?? this.refs.openButton;
    const top = anchor.getBoundingClientRect().top + window.scrollY - headerHeight();
    if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
  }

  /**
   * Phones: marks <html> while the filter row is stuck under the header, for its shadow. One
   * IntersectionObserver, no scroll listener: with the root's top cut at the header, a stuck row
   * pokes 1px above it and stops being fully visible.
   */
  /** A daily offer bar above the row changed height: measure the stuck line and the sidebar again. */
  #onBarHeight = () => {
    this.#stuckObserver?.disconnect();
    this.#watchStuck();
    this.#fitSidebar();
  };

  #watchStuck() {
    const row = this.querySelector('.wd-cf__row');
    if (!row) return;

    this.#stuckObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.rootBounds) return;
        const stuck = entry.intersectionRatio < 1 && entry.boundingClientRect.top < entry.rootBounds.top;
        document.documentElement.classList.toggle(BAR_STUCK, stuck);
      },
      { rootMargin: `-${headerHeight()}px 0px 0px 0px`, threshold: 1 }
    );
    this.#stuckObserver.observe(row);
  }

  #onPopState = () => {
    if (this.#isInPlace(window.location.href)) this.#navigate(window.location.href, { push: false });
  };

  get #isOpen() {
    return this.refs.panel.classList.contains('is-open');
  }

  /**
   * On a phone the quick links scroll sideways. Centre the active one when it is out of view,
   * unless an applied-filter pill fronts the row: then the row stays at its start so it is seen.
   */
  #revealActiveQuickLink() {
    const active = Array.from(this.querySelectorAll('.wd-cf__quick-link.is-active')).pop();
    const row = active?.parentElement;
    if (!(active instanceof HTMLElement) || !row || row.scrollWidth <= row.clientWidth) return;
    if (row.querySelector('.wd-cf__stack')) return;

    const start = active.offsetLeft - row.offsetLeft;
    if (start + active.offsetWidth <= row.clientWidth) return;

    row.scrollLeft = start - (row.clientWidth - active.offsetWidth) / 2;
  }

  /**
   * Menu and banner links use `?filter.p.tag=Medium2026Aug-Photography`. Shopify filters by it,
   * but Liquid only sees the top 100 tag values, so the snippet cannot show it as selected and its
   * links would drop it. A value that belongs to a group (and is that group's only value in the
   * URL, and the group has nothing selected yet) is moved into the tag path with one reload.
   * Several values of one group stay as they are: they mean "any of these".
   *
   * @returns {boolean} true when the page is being replaced.
   */
  #moveTagParamsToPath() {
    const state = this.#state;
    if (!state || window.Shopify?.designMode) return false;

    const url = new URL(window.location.href);
    const values = url.searchParams.getAll('filter.p.tag');
    if (!values.length || !url.pathname.startsWith(state.base)) return false;

    /** @type {Map<string, string>} handle -> group */
    const groupOf = new Map();
    for (const chip of this.refs.chips ?? []) {
      const { group, handle } = chip.dataset;
      if (group && handle) groupOf.set(handle, group);
    }

    /** @type {Map<string, { value: string, handle: string }[]>} */
    const byGroup = new Map();
    for (const value of values) {
      const handle = handleize(value);
      const group = groupOf.get(handle);
      if (!group) continue;
      byGroup.set(group, [...(byGroup.get(group) ?? []), { value, handle }]);
    }

    const moved = [];
    for (const [group, list] of byGroup) {
      if (list.length === 1 && !this.#current.groups.has(group)) moved.push(list[0]);
    }
    if (!moved.length) return false;

    const pathTags = decodeURIComponent(url.pathname.slice(state.base.length).replace(/^\//, ''))
      .split('+')
      .filter(Boolean);
    const handles = [...new Set([...pathTags, ...moved.map(({ handle }) => handle)])].sort();
    const keep = values.filter((value) => !moved.some((entry) => entry.value === value));

    url.searchParams.delete('filter.p.tag');
    url.searchParams.delete('page');
    for (const value of keep) url.searchParams.append('filter.p.tag', value);
    url.pathname = `${state.base}/${handles.join('+')}`;

    window.location.replace(url.toString());
    return true;
  }

  /** @param {Event} [event] */
  open(event) {
    if (DESKTOP.matches) {
      // The button only shows on desktop while the sidebar is hidden: bring the sidebar back.
      event?.preventDefault();
      this.toggleSidebar();
      return;
    }
    if (!this.#state) return;
    event?.preventDefault();

    const { panel, scrim, openButton, closeButton } = this.refs;

    this.#readState();
    this.#draft = { ...this.#current, groups: new Map(this.#current.groups) };
    this.#render();
    this.#setCount(null);

    panel.classList.add('is-open');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    scrim.hidden = false;
    openButton.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('wd-cf-locked');
    closeButton.focus({ preventScroll: true });
  }

  /** Desktop: hides or shows the sidebar; the product grid widens to four full-width columns. */
  toggleSidebar() {
    const hidden = document.documentElement.classList.toggle(SIDEBAR_HIDDEN);
    const { sidebarToggle, openButton } = this.refs;

    sidebarToggle?.setAttribute('aria-checked', String(!hidden));
    openButton.setAttribute('aria-expanded', String(!hidden));
    (hidden ? openButton : sidebarToggle)?.focus({ preventScroll: true });
  }

  /** @param {Event} [event] */
  close(event) {
    event?.preventDefault();
    if (!this.#isOpen) return;

    const { panel, scrim, openButton } = this.refs;

    panel.classList.remove('is-open');
    panel.setAttribute('role', 'region');
    panel.removeAttribute('aria-modal');
    scrim.hidden = true;
    openButton.setAttribute('aria-expanded', 'false');
    this.#unlock();
    this.#cancelCount();
    this.#clearHash();
    this.#draft = { ...this.#current, groups: new Map(this.#current.groups) };
    this.#render();
    openButton.focus({ preventScroll: true });
  }

  /** @param {KeyboardEvent} event */
  onPanelKeydown(event) {
    if (!this.#isOpen) return;

    if (event.key === 'Escape') {
      this.close(event);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      this.refs.panel.querySelectorAll('a[href], button:not([disabled]), input, summary')
    ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
    const first = /** @type {HTMLElement | undefined} */ (focusable[0]);
    const last = /** @type {HTMLElement | undefined} */ (focusable[focusable.length - 1]);
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * A group value. In the sidebar (or without the drawer) the link simply navigates.
   * @param {MouseEvent} event
   */
  pick(event) {
    if (!this.#isOpen) {
      this.go(event);
      return;
    }
    event.preventDefault();

    const chip = /** @type {HTMLElement} */ (event.target);
    const { group, handle } = chip.dataset;
    if (!group || !handle) return;

    if (this.#draft.groups.get(group) === handle) {
      this.#draft.groups.delete(group);
    } else {
      this.#draft.groups.set(group, handle);
    }

    this.#render();
    this.#scheduleCount();
  }

  /**
   * An artist (one handler on the list). Without the script the button submits the GET form.
   * @param {MouseEvent} event
   */
  pickArtist(event) {
    // Component hands handlers an event whose `target` is the element with on:click (the list).
    const button = event.composedPath().find((node) => node instanceof HTMLButtonElement);
    if (!(button instanceof HTMLButtonElement)) return;
    const name = button.value;

    if (!this.#isOpen) {
      this.#readState();
      if (!this.#state) return;
      event.preventDefault();
      this.#navigate(this.#draftUrl({ ...this.#current, vendor: name }));
      return;
    }
    event.preventDefault();

    this.#draft.vendor = this.#draft.vendor === name ? null : name;

    this.#render();
    this.#scheduleCount();
  }

  /**
   * With the script, artists are picked by click (pickArtist). Enter in the search box would
   * otherwise submit the form with the first artist's button.
   * @param {SubmitEvent} event
   */
  onArtistSubmit(event) {
    event.preventDefault();
  }

  /**
   * A sort option. In the sidebar the link simply navigates; in the drawer it joins the draft.
   * @param {MouseEvent} event
   */
  pickSort(event) {
    if (!this.#isOpen) {
      this.go(event);
      return;
    }
    event.preventDefault();

    this.#draft.sort = /** @type {HTMLElement} */ (event.target).dataset.sort ?? '';
    this.#render();
  }

  /**
   * A Search & Discovery value. In the sidebar the link (Shopify's url_to_add / url_to_remove)
   * simply navigates; in the drawer it joins the draft. Several values of one filter can be on.
   * @param {MouseEvent} event
   */
  pickApp(event) {
    if (!this.#isOpen) {
      this.go(event);
      return;
    }
    event.preventDefault();

    const pair = this.#appPair(/** @type {HTMLElement} */ (event.target));
    if (!pair) return;
    const [name, value] = pair;
    const app = this.#draft.app ?? [];
    const on = app.some(([key, other]) => key === name && other === value);
    this.#draft.app = on ? app.filter(([key, other]) => !(key === name && other === value)) : [...app, pair];

    this.#render();
    this.#scheduleCount();
  }

  /**
   * A price box changed. In the drawer it joins the draft; in the sidebar the form's Apply (or
   * Enter) applies it, so typing does not reload the grid.
   * @param {Event} event
   */
  onPriceChange(event) {
    if (!this.#isOpen) return;
    const form = /** @type {HTMLElement} */ (event.target).closest('form');
    if (!form) return;

    this.#draft.app = this.#withPrice(this.#draft.app ?? [], form);
    this.#render();
    this.#scheduleCount();
  }

  /**
   * The price form's Apply. Without the script it submits as a GET form that keeps the other filters.
   * @param {SubmitEvent} event
   */
  onPriceSubmit(event) {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.target);

    if (this.#isOpen) {
      this.#draft.app = this.#withPrice(this.#draft.app ?? [], form);
      this.#render();
      this.#scheduleCount();
      return;
    }

    this.#readState();
    if (!this.#state) {
      form.submit();
      return;
    }
    this.#navigate(this.#draftUrl({ ...this.#current, app: this.#withPrice(this.#current.app ?? [], form) }));
  }

  /**
   * The Search & Discovery pair a chip adds or removes, read from its link: the server rendered it
   * as this page's URL plus or minus that one value, so comparing the two gives the exact pair.
   * @param {HTMLElement} chip
   * @returns {[string, string] | null}
   */
  #appPair(chip) {
    const href = chip.getAttribute('href');
    if (!href) return null;

    /** @type {[string, string][]} */
    const link = [...new URL(href, window.location.href).searchParams];
    /** @type {[string, string][]} */
    const page = [...new URLSearchParams(window.location.search)];
    /** @param {[string, string][]} from @param {[string, string][]} other */
    const extra = (from, other) =>
      from.find(([key, value]) => isAppParam(key) && !other.some(([k, v]) => k === key && v === value));

    return extra(link, page) ?? extra(page, link) ?? null;
  }

  /**
   * `app` with the price form's amounts in place of any it had.
   * @param {[string, string][]} app
   * @param {HTMLElement} form
   * @returns {[string, string][]}
   */
  #withPrice(app, form) {
    let next = app;
    for (const input of form.querySelectorAll('input[data-price]')) {
      if (!(input instanceof HTMLInputElement) || !input.name) continue;
      const amount = toAmount(input.value);
      next = next.filter(([key]) => key !== input.name);
      if (amount) next = [...next, [input.name, amount]];
    }
    return next;
  }

  /**
   * Artist search. Accents are ignored, so "celestin" finds "Célestin Krier".
   * @param {InputEvent} event
   */
  filterArtists(event) {
    const query = fold(/** @type {HTMLInputElement} */ (event.target).value.trim());

    for (const item of this.refs.artistList?.children ?? []) {
      if (!(item instanceof HTMLElement)) continue;
      item.hidden = query !== '' && !fold(item.textContent ?? '').includes(query);
    }
  }

  /** @param {MouseEvent} event */
  clearDraft(event) {
    if (!this.#isOpen) return;
    event.preventDefault();

    this.#draft = { groups: new Map(), vendor: null, sort: this.#draft.sort };
    this.#render();
    this.#scheduleCount();
  }

  /** @param {MouseEvent} event */
  apply(event) {
    if (!this.#isOpen) return;
    event.preventDefault();

    this.#navigate(this.#draftUrl());
  }

  /** @returns {Draft} */
  #readSelection() {
    const groups = new Map();

    for (const chip of this.refs.chips ?? []) {
      const { group, handle } = chip.dataset;
      if (group && handle && chip.hasAttribute('aria-current')) groups.set(group, handle);
    }

    return {
      groups,
      vendor: this.#state?.vendors?.[0] ?? null,
      sort: this.#state?.sort ?? '',
      app: appPairs(this.#state?.app),
    };
  }

  /** Mirrors the draft onto the chips, the artist list, the sort options and the group summaries. */
  #render() {
    const labels = new Map();

    // Options greyed out by the server (data-empty) have no prints in the page's results. Once
    // the draft changes the filters, those counts no longer apply: let every option be picked.
    const { groups, vendor } = this.#draft;
    const changed =
      vendor !== this.#current.vendor ||
      groups.size !== this.#current.groups.size ||
      [...groups].some(([group, handle]) => this.#current.groups.get(group) !== handle) ||
      !samePairs(this.#draft.app, this.#current.app);

    for (const option of this.querySelectorAll('[data-empty]')) {
      if (option instanceof HTMLButtonElement) {
        option.disabled = !changed;
      } else if (changed) {
        option.removeAttribute('aria-disabled');
        option.removeAttribute('tabindex');
      } else {
        option.setAttribute('aria-disabled', 'true');
        option.setAttribute('tabindex', '-1');
      }
    }

    for (const chip of this.refs.chips ?? []) {
      const { group, handle, label } = chip.dataset;
      const on = Boolean(group && this.#draft.groups.get(group) === handle);

      chip.classList.toggle('is-active', on);
      chip.toggleAttribute('aria-current', on);
      if (on) labels.set(group, label ?? '');
    }

    // Search & Discovery values: several per filter; the group summary lists them all.
    const app = this.#draft.app ?? [];
    for (const chip of this.refs.appChips ?? []) {
      const pair = this.#appPair(chip);
      const on = Boolean(pair && app.some(([key, value]) => key === pair[0] && value === pair[1]));

      chip.classList.toggle('is-active', on);
      chip.toggleAttribute('aria-current', on);
      if (on && pair) labels.set(pair[0], [labels.get(pair[0]), chip.dataset.label].filter(Boolean).join(', '));
    }

    for (const input of this.refs.priceInputs ?? []) {
      if (!(input instanceof HTMLInputElement)) continue;
      const pair = app.find(([key]) => key === input.name);
      if (!pair) {
        input.value = '';
      } else if (Number(toAmount(input.value)) !== Number(pair[1])) {
        input.value = pair[1];
      }
    }

    for (const button of this.refs.artistList?.querySelectorAll('button') ?? []) {
      const on = button.value === this.#draft.vendor;

      button.classList.toggle('is-active', on);
      if (on) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    }

    for (const option of this.refs.sortOptions ?? []) {
      if ((option.dataset.sort ?? '') === this.#draft.sort) {
        option.setAttribute('aria-current', 'true');
      } else {
        option.removeAttribute('aria-current');
      }
    }

    if (this.#draft.vendor) labels.set('artist', this.#draft.vendor);

    for (const summary of this.refs.groupValues ?? []) {
      summary.textContent = labels.get(summary.dataset.group ?? '') ?? '';
    }

    this.refs.applyButton.setAttribute('href', this.#draftUrl());
  }

  /** @param {Draft} [draft] */
  #draftUrl(draft = this.#draft) {
    const state = /** @type {State} */ (this.#state);
    const handles = [...new Set([...(state.extras ?? []), ...draft.groups.values()].filter(Boolean))].sort();
    const params = new URLSearchParams();

    for (const tag of state.tags ?? []) {
      if (tag) params.append('filter.p.tag', tag);
    }
    for (const [key, value] of draft.app ?? []) params.append(key, value);
    if (draft.vendor) params.append('filter.p.vendor', draft.vendor);
    if (draft.sort) params.append('sort_by', draft.sort);

    const path = handles.length ? `${state.base}/${handles.join('+')}` : state.base;
    const query = params.toString();

    return query ? `${path}?${query}` : path;
  }

  #scheduleCount() {
    this.#cancelCount();
    this.refs.applyButton.setAttribute('aria-busy', 'true');
    this.#countTimer = window.setTimeout(() => this.#fetchCount(), COUNT_DELAY);
  }

  #cancelCount() {
    window.clearTimeout(this.#countTimer);
    this.#countRequest?.abort();
    this.#countRequest = null;
    this.refs.applyButton.removeAttribute('aria-busy');
  }

  async #fetchCount() {
    const request = new AbortController();
    this.#countRequest = request;

    const draft = new URL(this.#draftUrl(), window.location.origin);
    // Collection shortlist test (snippets/wd-short-ab-prepaint.liquid): count the page the shopper
    // will land on, which can drop the shortlist tag (an artist shows the full range). Before
    // `view` is added: target() leaves ?view= URLs alone.
    const corrected = window.droolPlpArm?.target(draft.href, false);
    const url = corrected ? new URL(corrected, window.location.origin) : draft;
    url.searchParams.set('view', 'wd-filter-count');

    try {
      const response = await fetch(url, { signal: request.signal });
      // A tag no product carries makes the whole tag path a 404: nothing matches.
      const count = response.status === 404 ? 0 : (await response.json()).count;

      if (request.signal.aborted) return;
      this.#setCount(typeof count === 'number' ? count : null);
    } catch (error) {
      if (request.signal.aborted) return;
      this.#setCount(null);
      window.droolReportError?.('wd-collection-filters', String(error));
    } finally {
      if (this.#countRequest === request) {
        this.#countRequest = null;
        this.refs.applyButton.removeAttribute('aria-busy');
      }
    }
  }

  /** @param {number | null} count - null keeps the label the server rendered for this page. */
  #setCount(count) {
    const button = this.refs.applyButton;
    if (!button.dataset.initialLabel) button.dataset.initialLabel = button.textContent?.trim() ?? '';

    if (count === null || !this.#state) {
      button.textContent = button.dataset.initialLabel;
      return;
    }

    const number = count.toLocaleString(document.documentElement.lang || undefined);
    button.textContent =
      count === 1 ? this.#state.seeOne : this.#state.seeOther.replace(COUNT_PLACEHOLDER, number);
  }

  #unlock() {
    document.documentElement.classList.remove('wd-cf-locked');
  }

  #clearHash() {
    if (window.location.hash !== `#${this.refs.panel.id}`) return;
    history.replaceState(history.state, '', window.location.pathname + window.location.search);
  }

  #onBreakpoint = () => {
    if (DESKTOP.matches) this.close();
  };
}

if (!customElements.get('wd-collection-filters')) {
  customElements.define('wd-collection-filters', WdCollectionFilters);
}
